import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ShieldUser } from 'lucide-react'
import { Auth } from './Auth'
import { TermsOfService } from './TermsOfService'
import { PrivacyPolicy } from './PrivacyPolicy'
import { PricingCard } from '../components/Landing/PricingCard'
import { FAQSection } from '../components/Landing/FAQSection'
import { redirectToCheckout, redirectToGuestCheckout } from '../lib/stripe-client'
import { supabase } from '../lib/supabase'

type ViewType = 'landing' | 'auth' | 'terms' | 'privacy'

export const LandingPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [currentView, setCurrentView] = useState<ViewType>('landing')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Determine view based on URL path
  React.useEffect(() => {
    if (location.pathname === '/signup' || location.pathname === '/signin') {
      setCurrentView('auth')
    } else {
      setCurrentView('landing')
    }
  }, [location.pathname])

  // Scroll to top whenever view changes
  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentView])

  // Determine if we should show signup form based on URL
  const showSignUp = location.pathname === '/signup'

  // Handle plan selection
  const handleSelectPlan = async (priceId: string) => {
    setError(null)
    setIsLoading(true)

    try {
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession()

      if (session) {
        // Existing user - use authenticated checkout
        await redirectToCheckout(priceId, session.access_token)
      } else {
        // New user - use guest checkout (no auth required)
        await redirectToGuestCheckout(priceId)
      }
    } catch (err) {
      console.error('Error selecting plan:', err)
      setError(err instanceof Error ? err.message : 'Failed to start checkout')
      setIsLoading(false)
    }
  }


  if (currentView === 'auth') {
    return (
      <Auth 
        onNavigateToTerms={() => setCurrentView('terms')}
        onNavigateToPrivacy={() => setCurrentView('privacy')}
        onBack={() => navigate('/')}
        defaultToSignUp={showSignUp}
      />
    )
  }

  if (currentView === 'terms') {
    return <TermsOfService onBack={() => setCurrentView('landing')} />
  }

  if (currentView === 'privacy') {
    return <PrivacyPolicy onBack={() => setCurrentView('landing')} />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-tertiary">
      {/* Header */}
      <header className="relative z-20 px-4 py-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-500 rounded-xl flex items-center justify-center shadow-lg">
              <ShieldUser className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-light-primary">BLP Assistant</h1>
              <p className="text-xs text-light-tertiary">Professional Sales Support</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/signin')}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
          >
            Sign In
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <main className="px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold text-light-primary mb-6">
              Master Your
              <br className="md:hidden" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-400"> BLP Sales Calls</span>
            </h2>
            <p className="text-xl text-light-secondary max-w-3xl mx-auto mb-8">
              Get instant, intelligent support for Beneficiary Liquidity Plan sales conversations. 
              Real-time answers, objection handling, and compliance guidance at your fingertips.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-xl font-semibold text-lg shadow-xl transition-all transform hover:scale-105"
              >
                View Pricing
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="px-8 py-4 bg-dark-tertiary hover:bg-dark-quaternary text-light-primary rounded-xl font-semibold text-lg border border-primary-600/30 transition-colors"
              >
                Start Free Trial
              </button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-dark-secondary/50 backdrop-blur-sm border border-primary-600/20 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-light-primary mb-4">Instant Answers</h3>
              <p className="text-light-secondary">
                Get immediate responses to any BLP question during live sales calls. Never miss a beat.
              </p>
            </div>

            <div className="bg-dark-secondary/50 backdrop-blur-sm border border-primary-600/20 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-light-primary mb-4">Compliance Ready</h3>
              <p className="text-light-secondary">
                All responses are vetted for compliance and regulatory accuracy. Stay confident and compliant.
              </p>
            </div>

            <div className="bg-dark-secondary/50 backdrop-blur-sm border border-primary-600/20 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m-7 8l4-4H4a1 1 0 01-1-1V3a1 1 0 011-1h16a1 1 0 011 1v8a1 1 0 01-1 1h-3l-4 4z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-light-primary mb-4">Expert Objection Handling</h3>
              <p className="text-light-secondary">
                Navigate difficult conversations with proven objection handling techniques and responses.
              </p>
            </div>
          </div>

          {/* Pricing Section */}
          <div id="pricing" className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-light-primary mb-4">
                Simple, Transparent Pricing
              </h2>
              <p className="text-xl text-light-secondary max-w-2xl mx-auto">
                Choose the plan that works best for you. Start with a 7-day free trial.
              </p>
            </div>

            {error && (
              <div className="mb-8 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-center">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
              <PricingCard
                title="Monthly"
                price={125}
                period="monthly"
                priceId={import.meta.env.VITE_STRIPE_MONTHLY_PRICE_ID || process.env.STRIPE_MONTHLY_PRICE_ID || ''}
                features={[
                  'Unlimited AI-powered assistance',
                  'Real-time answers during sales calls',
                  'Compliance-ready responses',
                  'Expert objection handling techniques',
                  'Access to all current and future features',
                  'Email support',
                  'Cancel anytime',
                ]}
                onSelectPlan={handleSelectPlan}
                isLoading={isLoading}
              />

              <PricingCard
                title="Annual"
                price={1250}
                period="annually"
                priceId={import.meta.env.VITE_STRIPE_ANNUAL_PRICE_ID || process.env.STRIPE_ANNUAL_PRICE_ID || ''}
                features={[
                  'Everything in Monthly plan',
                  'Save $250 per year (17% discount)',
                  'Priority email support',
                  'Early access to new features',
                  'Dedicated account manager',
                  'Quarterly strategy sessions',
                  'Custom integrations available',
                ]}
                popular
                onSelectPlan={handleSelectPlan}
                isLoading={isLoading}
              />
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-16">
            <FAQSection />
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-primary-600/20 to-secondary-600/20 backdrop-blur-sm border border-primary-600/30 rounded-3xl p-12 text-center">
            <h3 className="text-3xl font-bold text-light-primary mb-4">
              Ready to Close More BLP Sales?
            </h3>
            <p className="text-xl text-light-secondary mb-8 max-w-2xl mx-auto">
              Join industry professionals who are already using BLP Assistant to enhance their sales conversations and close more deals.
            </p>
            <button
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-12 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-xl font-bold text-xl shadow-2xl transition-all transform hover:scale-105"
            >
              View Pricing
            </button>
          </div>
        </div>
      </main>

      {/* Footer Links */}
      <footer className="px-4 py-8 border-t border-dark-tertiary/30">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-light-tertiary">
            © 2024 Listening Glass, LLC. All rights reserved.
          </div>
          <div className="flex items-center space-x-6 text-sm">
            <button
              onClick={() => setCurrentView('terms')}
              className="text-light-secondary hover:text-primary-400 transition-colors"
            >
              Terms of Service
            </button>
            <button
              onClick={() => setCurrentView('privacy')}
              className="text-light-secondary hover:text-primary-400 transition-colors"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </footer>
    </div>
  )
}